import React, { useState, useEffect, useMemo } from 'react';
import { Link, Routes, Route, useNavigate, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAppData } from '../../contexts/AppDataContext';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  DocumentTextIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  UserGroupIcon,
  BuildingStorefrontIcon
} from '@heroicons/react/24/outline';
import { downloadInvoicePDF } from '../../utils/pdfService';
import ReturnForm from './ReturnForm';
import ReturnDetail from '../../components/returns/ReturnDetail';
import ReturnEditForm from '../../components/returns/ReturnEditForm';

const ReturnsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { 
    getReturns, 
    deleteReturn, 
    fetchAllData,
    customers: customersState,
    suppliers: suppliersState,
  } = useAppData();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [returnsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('customer');
  const [returns, setReturns] = useState([]);
  const [customerReturns, setCustomerReturns] = useState([]);
  const [supplierReturns, setSupplierReturns] = useState([]);
  const [allReturns, setAllReturns] = useState([]);

  // Resolve entity display name and ID (client or supplier)
  const customers = customersState || [];
  const suppliers = suppliersState || [];
  const getEntityByReturn = (returnItem) => {
    if (!returnItem) return { name: '', id: null };
    
    // Use the entity name from the return if available
    const entityName = returnItem.entityName && returnItem.entityName.trim() !== '' ? 
      returnItem.entityName : '';
      
    const list = returnItem.type === 'supplier' ? suppliers : customers;
    const entity = list.find((e) => String(e.id) === String(returnItem.entityId));
    
    if (!entity) {
      return { 
        name: entityName || String(returnItem.entityId || ''),
        id: returnItem.entityId || null
      };
    }
    
    const full = `${entity.firstName || ''} ${entity.lastName || ''}`.trim();
    return { 
      name: full || entity.name || entity.company || String(entity.id),
      id: entity.id,
      entity: entity
    };
  };
  
  const getEntityDisplayNameByReturn = (returnItem) => {
    return getEntityByReturn(returnItem).name;
  };

  // Gérer le téléchargement du PDF
  const handleDownloadPDF = (returnItem) => {
    try {
      const mockInvoiceData = {
        invoiceNumber: returnItem.returnNumber || returnItem.id,
        issueDate: returnItem.date,
        items: returnItem.items || [{
          description: 'Article retourné',
          quantity: 1,
          price: parseFloat(returnItem.amount.replace(/[^0-9.-]+/g,'')),
          total: parseFloat(returnItem.amount.replace(/[^0-9.-]+/g,''))
        }],
        subtotal: parseFloat(returnItem.amount.replace(/[^0-9.-]+/g,'')),
        discount: 0,
        tax: 0,
        total: parseFloat(returnItem.amount.replace(/[^0-9.-]+/g,'')),
      };

      const mockEntityData = {
        firstName: returnItem.entity.split(' ')[0],
        lastName: returnItem.entity.split(' ').slice(1).join(' '),
        email: 'contact@example.com',
        phone: '+213 XXX XXX XXX',
      };

      downloadInvoicePDF(mockInvoiceData, mockEntityData);
      toast.success('Téléchargement du PDF démarré');
    } catch (error) {
      toast.error('Erreur lors du téléchargement du PDF');
      console.error(error);
    }
  };

  // Charger les données des retours
    const loadReturns = async () => {
      try {
      // Vérifier d'abord les données dans localStorage
      const returnsFromStorage = localStorage.getItem('stockpro_returns');
      let parsedReturns = [];
      
      if (returnsFromStorage) {
        try {
          parsedReturns = JSON.parse(returnsFromStorage);
          console.log('Données de retours trouvées dans localStorage:', parsedReturns.length);
        } catch (e) {
          console.error('Erreur lors de la lecture des retours depuis localStorage:', e);
        }
      } else {
        console.log('Aucune donnée de retours trouvée dans localStorage');
      }
      
      // Charger tous les retours depuis le contexte
        const allReturnsData = getReturns();
      console.log('Données de retours depuis le contexte:', allReturnsData.length);
      
      // Si les données du contexte sont vides mais localStorage a des données, utiliser localStorage
      const finalReturnsData = allReturnsData.length > 0 ? allReturnsData : parsedReturns;
      setAllReturns(finalReturnsData);
        
        // Séparer les retours clients et fournisseurs
      const customerData = finalReturnsData.filter(item => item.type === 'customer');
      const supplierData = finalReturnsData.filter(item => item.type === 'supplier');
        
        setCustomerReturns(customerData);
        setSupplierReturns(supplierData);
      
      console.log('Données de retours chargées - Total:', finalReturnsData.length, 
                 'Clients:', customerData.length, 
                 'Fournisseurs:', supplierData.length);
                 
      // Log détaillé des retours fournisseurs
      if (supplierData.length > 0) {
        console.log('Détail des retours fournisseurs:', supplierData);
      } else {
        console.log('Aucun retour fournisseur trouvé');
      }
      } catch (error) {
        console.error('Erreur lors du chargement des retours:', error);
        toast.error('Erreur lors du chargement des données');
      }
    };
    
  // Charger les données au montage du composant
  useEffect(() => {
    // Force-refresh data from localStorage
    fetchAllData().then(() => {
      loadReturns();
      console.log('Returns data loaded on component mount');
    });
  }, [getReturns, fetchAllData]);
  
  // Rafraîchir les données lorsque le composant devient visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchAllData().then(() => {
          loadReturns();
          console.log('Returns data refreshed on visibility change');
        });
      }
    };
    
    // Écouter les changements de stockage local (pour les modifications dans d'autres onglets)
    const handleStorageChange = (event) => {
      if (event.key === 'stockpro_returns' || event.key === null) {
        fetchAllData().then(() => {
          loadReturns();
          console.log('Returns data refreshed on storage change');
        });
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    
    // Rafraîchir les données toutes les 15 secondes (plus fréquent)
    const refreshInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchAllData().then(() => {
    loadReturns();
          console.log('Returns data refreshed on interval');
        });
      }
    }, 15000);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(refreshInterval);
    };
  }, [fetchAllData]);
  
  // Mettre à jour les retours affichés en fonction de l'onglet actif
  useEffect(() => {
    setReturns(activeTab === 'customer' ? customerReturns : supplierReturns);
  }, [activeTab, customerReturns, supplierReturns]);

  // Gérer les actions
  const handleView = (returnItem) => {
    navigate(`/returns/${returnItem.id}`, { state: { returnItem } });
  };

  const handleEdit = (returnItem) => {
    navigate(`/returns/${returnItem.id}/edit`, { state: { returnItem } });
  };

  const handleDelete = async (returnId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce retour ?')) {
      try {
        await deleteReturn(returnId);
        await fetchAllData();
        // Recharger les données des retours pour mettre à jour les statistiques et la répartition
        await loadReturns();
        toast.success('Retour supprimé avec succès');
      } catch (error) {
        console.error('Erreur lors de la suppression du retour:', error);
        toast.error('Erreur lors de la suppression du retour');
      }
    }
  };

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR');
  };
  
  // Fonction pour formater le montant
  const formatAmount = (amount) => {
    if (!amount && amount !== 0) return '0 DA';
    return `${amount.toLocaleString()} DA`;
  };
  
  const filteredReturns = returns.filter(returnItem => {
    // Vérifier si returnItem est défini et a les propriétés nécessaires
    if (!returnItem || !returnItem.returnNumber) return false;
    
    const entityName = getEntityDisplayNameByReturn(returnItem) || '';
    
    const matchesSearch = 
      (entityName && entityName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      returnItem.returnNumber.toLowerCase().includes(searchTerm.toLowerCase());
      
    if (!matchesSearch) return false;
    
    if (statusFilter === 'all') return true;
    if (statusFilter === 'completed') return returnItem.status === 'completed';
    if (statusFilter === 'pending') return returnItem.status === 'pending';
    if (statusFilter === 'rejected') return returnItem.status === 'rejected';
    return true;
  });
  
  const paginatedReturns = filteredReturns.slice((currentPage - 1) * returnsPerPage, currentPage * returnsPerPage);
  const totalReturns = filteredReturns.length;
  const totalPages = Math.ceil(totalReturns / returnsPerPage);

  // Statistiques des retours en temps réel
  const calculateReturnStats = (returnsData) => {
    // Calculer le montant total des retours
    const totalAmount = returnsData.reduce((sum, returnItem) => {
      const amount = returnItem.totalAmount || 0;
      return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
    }, 0);
    
    // Calculer le montant des retours acceptés
    const acceptedAmount = returnsData
      .filter(returnItem => returnItem.status === 'completed')
      .reduce((sum, returnItem) => {
        const amount = returnItem.totalAmount || 0;
        return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
      }, 0);
    
    // Obtenir le nombre d'entités uniques (clients ou fournisseurs)
    const uniqueEntityIds = new Set(returnsData.map(returnItem => returnItem.entityId));
    const entityCount = uniqueEntityIds.size;
    
    return {
      totalAmount,
      acceptedAmount,
      entityCount
    };
  };
  
  // Calculer les statistiques pour les clients et fournisseurs
  const customerStatsData = useMemo(() => calculateReturnStats(customerReturns), [customerReturns]);
  const supplierStatsData = useMemo(() => calculateReturnStats(supplierReturns), [supplierReturns]);
  
  // Créer les objets de statistiques pour l'affichage
  const customerStats = [
    {
      title: 'Total des Retours',
      value: `${formatAmount(customerStatsData.totalAmount)}`,
      icon: ChartBarIcon,
      color: 'from-red-500 to-red-600',
    },
    {
      title: 'Retours Acceptés',
      value: `${formatAmount(customerStatsData.acceptedAmount)}`,
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Clients Concernés',
      value: customerStatsData.entityCount.toString(),
      icon: UserGroupIcon,
      color: 'from-blue-500 to-blue-600',
    },
  ];

  const supplierStats = [
    {
      title: 'Total des Retours',
      value: `${formatAmount(supplierStatsData.totalAmount)}`,
      icon: ChartBarIcon,
      color: 'from-red-500 to-red-600',
    },
    {
      title: 'Retours Acceptés',
      value: `${formatAmount(supplierStatsData.acceptedAmount)}`,
      icon: CheckCircleIcon,
      color: 'from-green-500 to-green-600',
    },
    {
      title: 'Fournisseurs Concernés',
      value: supplierStatsData.entityCount.toString(),
      icon: BuildingStorefrontIcon,
      color: 'from-purple-500 to-purple-600',
    },
  ];

  const stats = activeTab === 'customer' ? customerStats : supplierStats;

  // Répartition par raisons de retour - Utilisation des données en temps réel
  const calculateReasonDistribution = (returnsData) => {
    // Définir les couleurs pour chaque raison
    const reasonColors = {
      'defective': 'from-red-400 to-red-500',
      'wrong_size': 'from-blue-400 to-blue-500',
      'wrong_item': 'from-yellow-400 to-yellow-500',
      'damaged': 'from-purple-400 to-purple-500',
      'expired': 'from-orange-400 to-orange-500',
      'customer_choice': 'from-green-400 to-green-500',
      'other': 'from-gray-400 to-gray-500'
    };
    
    // Compter les occurrences de chaque raison
    const reasonCounts = {};
    returnsData.forEach(returnItem => {
      const reason = returnItem.reason || 'other';
      if (!reasonCounts[reason]) {
        reasonCounts[reason] = 0;
      }
      reasonCounts[reason]++;
    });
    
    // Convertir en format attendu par l'UI
    const distribution = Object.entries(reasonCounts).map(([reason, count]) => ({
      name: getReasonLabel(reason),
      reason: reason,
      count: count,
      color: reasonColors[reason] || 'from-gray-400 to-gray-500'
    }));
    
    // Trier par nombre décroissant
    return distribution.sort((a, b) => b.count - a.count);
  };
  
  // Fonction pour obtenir le libellé d'une raison de retour
  const getReasonLabel = (reason) => {
    switch(reason) {
      case 'defective': return 'Défectueux';
      case 'wrong_size': return 'Mauvaise Taille';
      case 'wrong_item': return 'Mauvais Produit';
      case 'damaged': return 'Endommagé';
      case 'expired': return 'Expiré';
      case 'customer_choice': return 'Choix du client';
      default: return 'Autre';
    }
  };

  // Calculer la répartition pour les clients et fournisseurs
  const customerReasonDistribution = useMemo(() => calculateReasonDistribution(customerReturns), [customerReturns]);
  const supplierReasonDistribution = useMemo(() => calculateReasonDistribution(supplierReturns), [supplierReturns]);
  
  // Sélectionner la répartition en fonction de l'onglet actif
  const reasonDistribution = activeTab === 'customer' ? customerReasonDistribution : supplierReasonDistribution;

  return (
    <Routes>
      <Route index element={
        <div className="space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Retours
              </h1>
              <p className="text-white/60 mt-2">
                Gestion des retours clients et fournisseurs
              </p>
            </div>
            <Link
              to={`/returns/new?type=${activeTab}`}
              className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-red-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>{activeTab === 'customer' ? 'Nouveau Retour Client' : 'Nouveau Retour Fournisseur'}</span>
            </Link>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1">
              <button
                onClick={() => {
                  setActiveTab('customer');
                  setCurrentPage(1);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'customer'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ArrowUturnLeftIcon className="h-5 w-5" />
                <span>Retours Clients</span>
              </button>
              <button
                onClick={() => {
                  setActiveTab('supplier');
                  setCurrentPage(1);
                }}
                className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
                  activeTab === 'supplier'
                    ? 'bg-gradient-to-r from-red-600 to-blue-600 text-white shadow-lg'
                    : 'text-white/80 hover:bg-white/10 hover:text-white'
                }`}
              >
                <ArrowUturnRightIcon className="h-5 w-5" />
                <span>Retours Fournisseurs</span>
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white/60 text-sm font-medium">{stat.title}</p>
                    <p className="text-2xl font-bold text-white mt-2">{stat.value}</p>
                  </div>
                  <div className={`p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}>
                    <stat.icon className="h-6 w-6 text-white" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Répartition par raisons de retour */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20"
          >
            <h3 className="text-xl font-semibold text-white mb-4">
              Répartition par Raison de Retour
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {reasonDistribution.map((item) => (
                <div 
                  key={item.name}
                  className="bg-white/5 rounded-xl p-4 border border-white/10"
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="font-medium text-white">{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 bg-gradient-to-r ${item.color} text-white`}>
                      {item.count}
                    </div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full">
                    <div 
                      className={`h-3 rounded-full bg-gradient-to-r ${item.color}`}
                      style={{ width: `${(item.count / (returns.length || 1) * 100).toFixed(1)}%` }}
                    ></div>
                  </div>
                  <p className="mt-2 text-sm text-white/60">
                    {(item.count / (returns.length || 1) * 100).toFixed(1)}% des retours
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Search and Filters */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-white/50" />
                <input
                  type="text"
                  placeholder={`Rechercher un retour ${activeTab === 'customer' ? 'client' : 'fournisseur'}...`}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              {/* Status Filter */}
              <div className="flex items-center space-x-2">
                <FunnelIcon className="h-5 w-5 text-white/50" />
                <select
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="completed">Complétés</option>
                  <option value="pending">En attente</option>
                  <option value="rejected">Refusés</option>
                </select>
              </div>
            </div>
          </div>

          {/* Returns List */}
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 overflow-hidden">
            {returns.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <DocumentTextIcon className="h-16 w-16 text-white/30 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Aucun retour trouvé</h3>
                <p className="text-white/60 mb-6">Aucun résultat ne correspond à vos critères de recherche.</p>
                <Link
                  to={`/returns/new?type=${activeTab}`}
                  className="bg-gradient-to-r from-red-600 to-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:from-red-700 hover:to-blue-700 transition-all duration-300 flex items-center space-x-2"
                >
                  <PlusIcon className="h-5 w-5" />
                  <span>Créer un retour</span>
                </Link>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-white/5">
                      <tr>
                        <th className="px-6 py-4 text-left text-white font-semibold">N° Retour</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">{activeTab === 'customer' ? 'Client' : 'Fournisseur'}</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Date</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Montant</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Statut</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Raison</th>
                        <th className="px-6 py-4 text-left text-white font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/10">
                      {paginatedReturns.map((returnItem) => (
                        <motion.tr
                          key={returnItem.id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-white/5 transition-colors"
                        >
                          <td className="px-6 py-4 text-white font-mono text-sm">{returnItem.returnNumber}</td>
                          <td className="px-6 py-4 text-white font-medium">
                            {(() => {
                              const entityInfo = getEntityByReturn(returnItem);
                              return entityInfo.id && returnItem.type === 'customer' ? (
                                <NavLink 
                                  to={`/customers/${entityInfo.id}`}
                                  className="text-white hover:text-red-300 flex items-center"
                                >
                                  {entityInfo.name}
                                  <UserGroupIcon className="h-4 w-4 ml-1 opacity-70" />
                                </NavLink>
                              ) : entityInfo.id && returnItem.type === 'supplier' ? (
                                <NavLink 
                                  to={`/suppliers/${entityInfo.id}`}
                                  className="text-white hover:text-red-300 flex items-center"
                                >
                                  {entityInfo.name}
                                  <BuildingStorefrontIcon className="h-4 w-4 ml-1 opacity-70" />
                                </NavLink>
                              ) : (
                                entityInfo.name
                              );
                            })()} 
                          </td>
                          <td className="px-6 py-4 text-white/80">{formatDate(returnItem.date)}</td>
                          <td className="px-6 py-4 text-white font-medium">{formatAmount(returnItem.totalAmount)}</td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              returnItem.status === 'completed' 
                                ? 'bg-green-500/20 text-green-400' 
                                : returnItem.status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-400'
                                : 'bg-red-500/20 text-red-400'
                            }`}>
                              {returnItem.status === 'completed' ? 'Accepté' : 
                               returnItem.status === 'pending' ? 'En attente' : 'Refusé'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400`}>
                              {returnItem.reason === 'defective' ? 'Défectueux' : 
                               returnItem.reason === 'wrong_size' ? 'Mauvaise taille' : 
                               returnItem.reason === 'wrong_item' ? 'Erreur produit' :
                               returnItem.reason === 'expired' ? 'Expiré' : 'Endommagé'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleView(returnItem)}
                                className="p-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors"
                                title="Voir les détails"
                              >
                                <EyeIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDownloadPDF(returnItem)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Télécharger en PDF"
                              >
                                <ArrowDownTrayIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(returnItem)}
                                className="p-2 bg-yellow-500/20 text-yellow-400 rounded-lg hover:bg-yellow-500/30 transition-colors"
                                title="Modifier"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(returnItem.id)}
                                className="p-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                                title="Supprimer"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 flex items-center justify-between border-t border-white/10">
                    <div className="text-sm text-white/60">
                      Affichage de {(currentPage - 1) * returnsPerPage + 1} à {Math.min(currentPage * returnsPerPage, totalReturns)} sur {totalReturns} retours
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Précédent
                      </button>
                      <button
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 bg-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Suivant
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      } />
      {/* Routes pour la création et l'édition */}
      <Route path="new" element={<ReturnForm />} />
      <Route path=":id" element={<ReturnDetail />} />
      <Route path=":id/edit" element={<ReturnEditForm />} />
    </Routes>
  );
};

export default ReturnsPage; 